import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getBootstrap } from "@/lib/bootstrap";
import { cn } from "@/lib/utils";
import { useMainMenu } from "@/hooks/useMainMenu";
import { useSiteOptions } from "@/hooks/useSiteOptions";

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const menu = useMainMenu();
  const { phone } = useSiteOptions();
  const { page, site } = getBootstrap();
  const siteName = site.name || "Site";
  const currentPath = normalizePath(page.path);

  useEffect(() => {
    const onPageShow = () => setOpen(false);
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  return (
    <header className="sticky top-0 inset-x-0 z-50 bg-background border-b border-border/40">
      <div className="container flex h-20 items-center justify-between gap-6">
        <a href="/" aria-label={`${siteName} - Accueil`}>
          <span className="font-display font-black text-xl md:text-2xl uppercase leading-none text-foreground">
            {siteName}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-2" aria-label="Navigation principale">
          {menu.map((item) => {
            const isActive = normalizePath(item.url) === currentPath;

            return (
              <a
                key={item.id}
                href={item.url}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "px-4 py-2 text-sm font-semibold uppercase tracking-wide rounded-sm border transition-smooth",
                  isActive
                    ? "border-foreground text-foreground"
                    : "border-transparent text-foreground/85 hover:text-foreground hover:border-foreground/30",
                )}
              >
                {item.title}
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {phone && (
            <a
              href={`tel:${phone.e164}`}
              className="hidden sm:inline-flex items-center justify-center rounded-sm bg-gradient-fire px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-fire transition-smooth hover:opacity-95"
            >
              {phone.national}
            </a>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border/40 bg-background animate-fade-up">
          <nav className="container py-4 flex flex-col gap-1" aria-label="Navigation mobile">
            {menu.map((item) => {
              const isActive = normalizePath(item.url) === currentPath;

              return (
                <a
                  key={item.id}
                  href={item.url}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "px-4 py-3 text-base font-semibold uppercase tracking-wide rounded-sm",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-foreground/85 hover:bg-muted",
                  )}
                >
                  {item.title}
                </a>
              );
            })}
            {phone && (
              <a
                href={`tel:${phone.e164}`}
                className="mt-2 inline-flex items-center justify-center rounded-sm bg-gradient-fire px-4 py-3 text-base font-bold text-primary-foreground shadow-fire"
              >
                {phone.national}
              </a>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

function normalizePath(path: string): string {
  if (!path) return "/";
  const pathname = path.startsWith("http") ? new URL(path).pathname : path;
  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export default SiteHeader;
